import { useState, useEffect, useRef } from "react";
import {  useLocation, useNavigate } from "react-router-dom";
import encryptedapiRequest from "../../lib/encryptedapiRequest";
import { usePropertyListContext } from "../../context/PropertyListContext";

/**
 * Custom hook for paginated lists with:
 * - Infinite scroll
 * - Filter changes
 * - State persistence via context
 * - Restoration on back navigation
 * 
 * Handles three scenarios:
 * 1. First visit from menu/home with navFilters → fetch with filters, clear location.state
 * 2. Back navigation (restored state) → restore without fetching
 * 3. Default fetch (first visit, no navFilters) → fetch page 1
 * 
 * Please do not forget to add fromMenu=true from parent
 */




export default function usePaginatedList(
  endpoint,
  {
    limit = 10,
    initialFilters = {},
    scrollContainerSelector,
    storageKey,
    skipInitialFetch = false,
    initialPage = 1,
    onRestore,
  } = {}
) {
  const location = useLocation();
  const { getList, updateList } = usePropertyListContext();
  const navigate=useNavigate();

  const fromMenu = location.state?.fromMenu || false;

  // console.log("From Menu: ", fromMenu);

  // console.log("Location.state ", location.state);


  // 🔹 Get restored state from context if available
  const restoredState = storageKey ? getList(storageKey) : null;

  // 🔹 Filters passed via navigation (menu/home)
  // const navFilters = location.state
  //   ? { ...initialFilters, ...location.state }
  //   : null;

const navFilters = location.state && !fromMenu
  ? { ...initialFilters, ...location.state }
  : null;

  // 🔹 Determine if restoration is eligible
  // const isRestoredEligible = restoredState && !navFilters;
  // const isRestoredEligible = restoredState && restoredState.items?.length > 0 && !navFilters;

const isRestoredEligible =
  restoredState &&
  restoredState.items?.length > 0 &&
  !navFilters &&
  !fromMenu;

// console.log("isRestoredEligible: ", isRestoredEligible);

const ignorePageEffectRef = useRef(false);
const scrollEnabled = useRef(false); // 🟢 prevent auto scroll-triggered fetch on initial render
const filterResetting = useRef(false);
const bottomTriggeredRef = useRef(false);
const initialFetchSettled = useRef(false); // ✅ prevent scroll-triggered fetch until first paint
const userHasScrolled = useRef(false);


  // 🔹 Local state
const [items, setItems] = useState(
  isRestoredEligible ? restoredState?.items : []
);

  // const [page, setPage] = useState(restoredState?.page || initialPage);
  const [page, setPage] = useState(
    isRestoredEligible ? restoredState?.page : initialPage
  );

  const [filters, setFilters] = useState(
    isRestoredEligible
      ? restoredState?.filters
      : navFilters || initialFilters
  );

  const [totalPages, setTotalPages] = useState(restoredState?.totalPages || 1);
  const [loading, setLoading] = useState(!skipInitialFetch && !isRestoredEligible);
  const [reloading, setReloading] = useState(false);
  const [error, setError] = useState(null);

  // 🔹 Refs
  const didMountRef = useRef(false);        // tracks first mount
  const didInitialFetchRef = useRef(false); // prevents double fetch on first mount
  const isFetching = useRef(false);         // prevents concurrent fetch
  const activeFilters = useRef(filters);    // latest filters used for fetch




  // useEffect(() => {
  //   console.log("🟢 MOUNTED usePaginatedList");
  //   console.log("Initial filters:", filters);
  //   console.log("Initial page:", page);
  //   console.log("skipInitialFetch:", skipInitialFetch);
  // }, []);





  // -----------------------------------------------------
  // Helper: remove empty or invalid filters
  // -----------------------------------------------------
  const cleanFilters = (obj) => {
    const cleaned = {};
    for (let key in obj) {
      if (Array.isArray(obj[key]) && obj[key].length === 0) continue;
      if (obj[key] === "" || obj[key] == null) continue;
      cleaned[key] = obj[key];
    }
    return cleaned;
  };

  // -----------------------------------------------------
  // Fetch data function (single source of truth)
  // -----------------------------------------------------
  const fetchItems = async (pageNumber = 1, newFilters = activeFilters.current) => {
    // console.log("📡 FETCH CALLED");
    // console.log("Fetching page:", pageNumber);
    // console.log("Filters used:", newFilters);


    const flattenedFilters = cleanFilters(newFilters);
    const isFirstLoad = pageNumber === 1;


    let requestFailed = false;

    if (isFirstLoad) setLoading(true);
    else setReloading(true);

    try {
      const res = await encryptedapiRequest.get(endpoint, {
        params: { page: pageNumber, limit, ...flattenedFilters },
      });

      const { data, totalPages: total } = res.data || {};
      const newItems = Array.isArray(data) ? data : [];

      // console.log("✅ FETCH SUCCESS page:", pageNumber);
      // console.log("Received items:", newItems.length);
      // console.log("Total pages from server:", total);


      // if (isFirstLoad) setItems(newItems);
      // else setItems((prev) => [...prev, ...newItems]);

      if (isFirstLoad) {
        setItems(newItems);
        // scrollEnabled.current = true; // ✅ enable scroll-triggered fetching now
        // ✅ wait for DOM to paint before enabling scroll-triggered fetch
        requestAnimationFrame(() => {
            initialFetchSettled.current = true;
            scrollEnabled.current = true;
        });


      } else {
        setItems((prev) => [...prev, ...newItems]);
      }

      setTotalPages(total || 1);
      setError(null);

    } catch (err) {
      requestFailed = true;
      setError(err);
      console.error("Pagination fetch error:", err);
    } finally {
      if (isFirstLoad) setLoading(false);
      else setReloading(false);

      if (requestFailed && pageNumber > 1) {
        setPage((prev) => prev - 1);
      }

      isFetching.current = false;
    }
  };

  const refresh = (pageNumber = 1, newFilters = activeFilters.current) => {
    setPage(pageNumber);
    return fetchItems(pageNumber, newFilters);
  };



const restoreScroll = (scrollTop, selector) => {
  const container = document.querySelector(selector);
  if (!container) return;

  const tryScroll = () => {
    // Scroll only if container has enough height
    if (container.scrollHeight >= scrollTop) {
      container.scrollTo({ top: scrollTop, behavior: "auto" });
    } else {
      requestAnimationFrame(tryScroll); // retry until content renders
    }
  };

  requestAnimationFrame(tryScroll);
};



  // -----------------------------------------------------
  // INITIAL LOAD & RESTORATION LOGIC
  // -----------------------------------------------------
  useEffect(() => {
    if (didMountRef.current) return;
    didMountRef.current = true;

      // console.log("🔵 INITIAL LOAD EFFECT");
      // console.log("restoredState:", restoredState);
      // console.log("navFilters:", navFilters);
      // console.log("fromMenu:", fromMenu);
      // console.log("isRestoredEligible:", isRestoredEligible);
      // console.log("skipInitialFetch:", skipInitialFetch);
      // console.log("initialPage:", initialPage);
      // console.log("current page state:", page);
      // console.log("current filters:", filters);

    // 1️⃣ Nav filters exist → first visit from menu/home
    if (navFilters) {
      // console.log("→ Loading with navFilters (first visit from menu/home)");

      activeFilters.current = navFilters;
      setFilters(navFilters);

      userHasScrolled.current = false; // ✅ reset for fresh load


      fetchItems(1, navFilters).then(() => {
        // Clear navFilters from location.state to allow restoration later
        if (location.state) location.state = null;
        didInitialFetchRef.current = true;
        // console.log("→ Page 1 fetched with navFilters");
      });
      return;
    }

    // console.log("🔍 Checking restoration...");
    // console.log("restoredState exists:", !!restoredState);
    // console.log("navFilters exists:", !!navFilters);
    // console.log("isRestoredEligible:", isRestoredEligible);

    // 2️⃣ Restoration eligible → back navigation
    if (isRestoredEligible) {

      //  console.log("→ Restoring previous state from context");

      // console.log("🟢 RESTORING STATE"ds);
      // console.log("Restored items length:", restoredState.items?.length);
      // console.log("Restored page:", restoredState.page);
      // console.log("Restored filters:", restoredState.filters);
      // console.log("Restored scrollTop:", restoredState.scrollTop);

      
      activeFilters.current = restoredState.filters;
      setItems(restoredState.items);
      setPage(restoredState.page);
      setFilters(restoredState.filters);
      
    // restoreScroll(restoredState.scrollTop || 0, scrollContainerSelector);

    if (onRestore) {
        onRestore(restoredState);
      }




      // requestAnimationFrame(() => {
      //    console.log("🟢 RESTORATION SCROLL POSITION:", restoredState.scrollTop);
      //   document.querySelector(scrollContainerSelector)?.scrollTo({
      //     top: restoredState.scrollTop || 0,
      //     behavior: "auto",
      //   });
      //   didInitialFetchRef.current = true;
      // });

        // didInitialFetchRef.current = true;

      setTimeout(() => {
        didInitialFetchRef.current = true;

        initialFetchSettled.current = true; // ✅ FIX
        scrollEnabled.current = true;       // ✅ FIX
        filterResetting.current = false;    // ✅ FIX
        isFetching.current = false;         // ✅ FIX
      }, 0);

      return;
    // }else{

    //   refresh(1, filters); // triggers the API fetch with default filters
    }


  // console.log("→ Default fetch (first visit without navFilters)");


    // 3️⃣ Default fetch → first visit without navFilters
    activeFilters.current = filters;

    userHasScrolled.current = false; // ✅ ADD HERE

    fetchItems(1, filters).then(() => {
      didInitialFetchRef.current = true;
    });

  }, []);

  // -----------------------------------------------------
  // FILTER CHANGES (ApplyFilters)
  // -----------------------------------------------------
useEffect(() => {
  // if (!didInitialFetchRef.current) {
  //   return; // 🚀 skip first mount
  // }
  // console.log("filter effect")
 if (!didInitialFetchRef.current) return; // skip first mount

  // console.log("🟡 FILTER EFFECT TRIGGERED");
  // console.log("New filters:", filters);
  ignorePageEffectRef.current = true; // skip page effect temporarily
  filterResetting.current = true; // ✅ mark filter change in progress

  activeFilters.current = filters;
  setPage(1);

 // 🔹 Reset restoredPageRef so infinite scroll works correctly
  restoredPageRef.current = null;
  scrollEnabled.current = false; // 🔹 prevent auto fetch until page 1 is fully rendered
  // fetchItems(1, filters);
  fetchItems(1, filters).finally(() => {
    ignorePageEffectRef.current = false; // allow page effect again
    filterResetting.current = false; // ✅ done resetting
    userHasScrolled.current = false; // ✅ reset scroll guard
    scrollEnabled.current = true;        // enable scroll now
    isFetching.current = false; // prevent leftover flags
  });

}, [endpoint, JSON.stringify(filters)]);

  // -----------------------------------------------------
  // INFINITE SCROLL (NEXT PAGE)
  // -----------------------------------------------------
  const restoredPageRef = useRef(isRestoredEligible ? restoredState.page : null);


  useEffect(() => {
    if (!initialFetchSettled.current) return;
    if (page === 1) return;
    if (page > totalPages) return;

     if (ignorePageEffectRef.current) return; // skip if triggered by filter reset

    // 🔹 Skip fetch if restored
    if (restoredPageRef.current != null && page <= restoredPageRef.current) {
      // console.log("⛔ Skipping fetch for restored page:", page);
      return;
    }

    // console.log("🟣 PAGE EFFECT → fetching page:", page);
    isFetching.current = true;
    fetchItems(page, activeFilters.current);

  }, [page, totalPages]);

  // -----------------------------------------------------
  // SCROLL HANDLER
  // -----------------------------------------------------

useEffect(() => {
  if (!scrollContainerSelector || !storageKey) return;

  const container = document.querySelector(scrollContainerSelector);
  if (!container) return;

  const handleClick = () => {
    // console.log("💾 Saving scroll at click:", container.scrollTop);

    updateList(storageKey, {
      items,
      page,
      filters,
      scrollTop: container.scrollTop,
      totalPages,
    });
  };

  container.addEventListener("click", handleClick);

  return () => container.removeEventListener("click", handleClick);
}, [scrollContainerSelector, items, page, filters, totalPages, storageKey]);





  useEffect(() => {
    if (!scrollContainerSelector) return;

    const el = document.querySelector(scrollContainerSelector);
    if (!el) return;


    const handleScroll = () => {
      // 🔹 skip until scroll fetching is ready AND first page is rendered
      if (!scrollEnabled.current || !initialFetchSettled.current) return; // 🔹 skip until enabled
      if (isFetching.current || loading) return;
      if (page >= totalPages) return;
      if (filterResetting.current) return; // 🚫 skip scroll until page 1 done

      const { scrollTop, scrollHeight, clientHeight } = el;
      // ✅ mark real scroll
      if (scrollTop > 0) {
        userHasScrolled.current = true;
      }

      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 120;

      // console.log("⬇️ SCROLL EVENT", { scrollTop, scrollHeight, clientHeight, isNearBottom, page });


      // if (scrollTop + clientHeight >= scrollHeight - 120) {
      //   isFetching.current = true;
      //   setPage((prev) => prev + 1);
      // }
      // if (isNearBottom && !bottomTriggeredRef.current) {
   
      if (
          // isNearBottom &&
          // scrollTop > 0 &&   // ✅ ONLY trigger if user actually scrolled
          // !bottomTriggeredRef.current
          userHasScrolled.current &&   // ✅ NEW GUARD
          isNearBottom &&
          !bottomTriggeredRef.current



        ){
          // console.log("→ Near bottom, triggering next page fetch", page + 1);

          bottomTriggeredRef.current = true;   // 🔒 lock
          isFetching.current = true;
          setPage((prev) => prev + 1);
      }

      // 🔓 unlock only if user scrolls up again
        if (!isNearBottom) {
          bottomTriggeredRef.current = false;
        }



    };

    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);

  }, [loading, page, totalPages, scrollContainerSelector]);


const didRestoreScroll = useRef(false);

// useEffect(() => {
//   if (!isRestoredEligible) return;
//   if (didRestoreScroll.current) return;
//   if (!items.length) return;

//   const container = document.querySelector(scrollContainerSelector);
//   if (!container) return;

//   const targetScroll = restoredState?.scrollTop || 0;

//   console.log("📌 SCROLL RESTORE ATTEMPT");
//   console.log("Target scrollTop:", targetScroll);
//   console.log("Current scrollHeight:", container.scrollHeight);
//   console.log("Current clientHeight:", container.clientHeight);


//   const tryRestore = () => {
//     // wait until content is tall enough
//     console.log("🔁 Checking scrollHeight:", container.scrollHeight);

//     if (container.scrollHeight >= targetScroll) {
//       container.scrollTo({ top: targetScroll, behavior: "auto" });
//       didRestoreScroll.current = true;
//       // console.log("✅ Scroll restored at:", targetScroll);
//       console.log("✅ SCROLL RESTORED");
//       console.log("Final scrollTop:", container.scrollTop);

//     } else {
//       requestAnimationFrame(tryRestore);
//     }
//   };

//   requestAnimationFrame(tryRestore);

// }, [items.length,isRestoredEligible]);




useEffect(() => {
  if (!isRestoredEligible) return;
  if (didRestoreScroll.current) return;
  if (!items.length) return;

  const targetScroll = restoredState?.scrollTop || 0;

  let animationId = null;
  let attempts = 0;
  const maxAttempts = 60;

  const tryRestore = () => {
    const container = document.querySelector(scrollContainerSelector);
    if (!container) return;

    // console.log("🔁 Checking scrollHeight:", container.scrollHeight);

    if (container.scrollHeight === 0) {
      if (attempts++ < maxAttempts) {
        animationId = requestAnimationFrame(tryRestore);
      }
      return;
    }

    if (container.scrollHeight >= targetScroll) {
      container.scrollTo({
        top: targetScroll,
        behavior: "auto",
      });

      didRestoreScroll.current = true;
      // console.log("✅ SCROLL RESTORED");
      return;
    }

    if (attempts++ < maxAttempts) {
      animationId = requestAnimationFrame(tryRestore);
    }
  };

  animationId = requestAnimationFrame(tryRestore);

  // ✅ CLEANUP (THIS WAS MISSING)
  return () => {
    if (animationId) {
      cancelAnimationFrame(animationId);
    }
  };

}, [items.length, isRestoredEligible]);




  // -----------------------------------------------------
  // CONTEXT PERSISTENCE
  // -----------------------------------------------------
  // useEffect(() => {
  //   if (!storageKey) return;

  //     const container = document.querySelector(scrollContainerSelector);
  //     const currentScrollTop = container?.scrollTop || 0;
  
  //     console.log("💾 SAVING LIST STATE");
  //     // console.log("Items length:", items.length);
  //     // console.log("Page:", page);
  //     // console.log("Filters:", filters);
  //     // console.log("TotalPages:", totalPages);
  //     console.log("ScrollTop being saved:", currentScrollTop);
  //     console.log("ScrollHeight at save:", container?.scrollHeight);




  //   updateList(storageKey, {
  //     items,
  //     page,
  //     filters,
  //     totalPages,
  //     scrollTop: document.querySelector(scrollContainerSelector)?.scrollTop || 0,
  //   });
  // }, [items, page, filters, totalPages]);

useEffect(() => {
  console.log("🔵 PAGE STATE CHANGED →", page);
}, [page]);


// Clear location.state after reading
useEffect(() => {
  if (location.state) {
    navigate(location.pathname, { replace: true, state: null });
  }
}, [location.state, navigate]);


  // -----------------------------------------------------
  // APPLY NAVIGATION FILTERS MANUALLY
  // -----------------------------------------------------
  const applyNavFilters = (newFilters) => {
    activeFilters.current = newFilters;
    setFilters(newFilters);
    setPage(1);
    // fetchItems(1, newFilters);
  };

  // -----------------------------------------------------
  // RETURNED HOOK API
  // -----------------------------------------------------
  return {
    items,
    setItems,
    page,
    setPage,
    setFilters,
    loading,
    reloading,
    error,
    hasMore: page < totalPages,
    refresh: () => fetchItems(1, activeFilters.current),
    applyNavFilters,
  };
}
